import {
  CompanyEntity,
  getObjectId,
  SettlementStatus,
  settlementStatus,
  toSettlementStatus,
  toTransportMethod,
  TransportMethod
} from "@ots-frontend/ots-data";
import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Modal, ModalProps, TextInput} from "@mantine/core";
import {useCallback} from "react";
import {SettlementStatusSelect} from "../../select/entity/SettlementStatusSelect/SettlementStatusSelect";
import {CompanySelect} from "../../select/entity/CompanySelect/CompanySelect";
import {TransportMethodSelect} from "../../select/entity/TransportMethodSelect/TransportMethodSelect";

interface CompanySettlementFormProps {
  options?: CompanySettlementFormOptions;
  defaultValues?: Partial<CompanySettlementFormValues>;
  onSubmit?: (values: CompanySettlementFormValues) => void;
  submitting?: boolean;
}

export interface CompanySettlementFormOptions {
  regionId?: string
  methodDisabled?: boolean
}

export interface CompanySettlementFormValues {
  name?: string
  company: CompanyEntity | string;
  status: SettlementStatus
  method: TransportMethod
}

const getDefaultValues = (defaultValues: Partial<CompanySettlementFormValues> | undefined) => {
  return {
    name: defaultValues?.name,
    company: getObjectId(defaultValues?.company),
    status: defaultValues?.status?.code ?? settlementStatus.open.code,
    method: defaultValues?.method?.code,
  }
}

export const CompanySettlementForm = (props: CompanySettlementFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useForm({
    initialValues: getDefaultValues(defaultValues),
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        name: values.name,
        company: values.company!,
        status: toSettlementStatus(values.status!),
        method: toTransportMethod(values.method!),
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <CompanySelect
        label={t("common.entity.companysettlement.form.field.company", "Company")}
        placeholder={t<string>("common.entity.companysettlement.form.field.type.company", "Company")}
        styles={{wrapper: {width: "100%"}}}
        selector={{
          regionId: options?.regionId,
        }}
        withAsterisk={true}
        required={true}
        clearable={true}
        withinPortal={true}
        {...form.getInputProps("company")} />
      <TextInput
        label={t("common.entity.companysettlement.form.field.name", "Name")}
        placeholder={t<string>("common.entity.companysettlement.form.field.name.placeholder", "Name")}
        styles={{wrapper: {width: "100%"}}}
        {...form.getInputProps("name")} />
      <Divider my="sm" variant="dotted"/>
      <TransportMethodSelect
        disabled={options?.methodDisabled && typeof defaultValues?.method !== "undefined"}
        label={t("common.entity.companysettlement.form.field.method", "Method")}
        placeholder={t<string>("common.entity.companysettlement.form.field.type.method", "Method")}
        styles={{wrapper: {width: "100%"}}}
        withAsterisk={true}
        required={true}
        clearable={true}
        withinPortal={true}
        {...form.getInputProps("method")} />
      <Divider my="sm" variant="dotted"/>
      <SettlementStatusSelect
        label={t("common.entity.companysettlement.form.field.status", "Status")}
        placeholder={t<string>("common.entity.companysettlement.form.field.status.placeholder", "Status")}
        styles={{wrapper: {width: "100%"}}}
        withAsterisk={true}
        required={true}
        clearable={true}
        withinPortal={true}
        {...form.getInputProps("status")} />
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface CompanySettlementFormModalProps extends CompanySettlementFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const CompanySettlementFormModal = (props: CompanySettlementFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    options,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <CompanySettlementForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}
                             options={options}/>
    </Modal>
  );
};
